import React, { useEffect, useState } from "react";
import styles from "./FAQSection.module.css"; // Import CSS Module
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import API_BASE_URL from "../../config/api";

const FAQSection = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [typeFilter, setTypeFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetch(`${API_BASE_URL}/fetchFaq.php`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data.error) {
          throw new Error(data.error);
        }
        setFaqs(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching FAQs:", error);
        setError(error.message);
        setLoading(false);
      });
  }, []);

  const toggleAnswer = (faqId) => {
    setExpanded((prev) => (prev === faqId ? null : faqId));
  };

  const handleTypeFilterChange = (type) => {
    setTypeFilter(type);
    setExpanded(null);
  };

  const handleCategoryFilterChange = (category) => {
    setCategoryFilter(category);
    setExpanded(null);
  };

  const uniqueQuestionTypes = [
    "All",
    ...new Set(faqs.map((faq) => faq.question_type).filter((type) => type)),
  ];

  const uniqueCategories = [
    "All",
    ...new Set(faqs.map((faq) => faq.category).filter((category) => category)),
  ];

  const filteredFaqs = faqs.filter((faq) => {
    const matchesType =
      typeFilter === "All" || faq.question_type === typeFilter;
    const matchesCategory =
      categoryFilter === "All" || faq.category === categoryFilter;
    const matchesSearch = faq.question
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesType && matchesCategory && matchesSearch;
  });

  if (loading) {
    return <p>Loading FAQs...</p>;
  }

  if (error) {
    return <p>Error: {error}</p>;
  }

  return (
    <div className={styles.faqSection}>
      <h1 className={styles.h1}>Frequently Asked Questions</h1>

      <div className={styles.faqUpperContainer}>
        <div className={styles.faqFilters1}>
          {uniqueQuestionTypes.map((type, index) => (
            <button
              key={index}
              className={`${styles.filterBtn1} ${
                typeFilter === type ? styles.filterBtn1Active : ""
              }`}
              onClick={() => handleTypeFilterChange(type)}
            >
              {type === "All" ? "All" : type}
            </button>
          ))}
        </div>
        {/* Filter by Question Type */}
        <div className={styles.faqSearchContainer}>
          <input
            type="text"
            className={styles.faqSearchBar}
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Filter by Category */}
      <div className={styles.faqFilters2}>
        {uniqueCategories.map((category, index) => (
          <React.Fragment key={index}>
            <button
              className={`${styles.filterBtn2} ${
                categoryFilter === category ? styles.filterBtn2Active : ""
              }`}
              onClick={() => handleCategoryFilterChange(category)}
            >
              {category === "All" ? "All" : category}
            </button>
            {index < uniqueCategories.length - 1 && (
              <span className={styles.separator}></span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Divider */}
      <div className={styles.divider}></div>

      {filteredFaqs.length === 0 ? (
        <p>No FAQs available for this filter combination.</p>
      ) : (
        <ul className={styles.faqList}>
          {filteredFaqs.map((faq) => {
            return (
              <li key={faq.faq_id} className={styles.faqItem}>
                <div
                  className={styles.faqHeader}
                  onClick={() => toggleAnswer(faq.faq_id)}
                >
                  <div className={styles.faqQuestion}>
                    <span>{faq.question}</span>
                  </div>
                  <button className={styles.faqToggleBtn}>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`${styles.faqToggleBtn} ${
                        expanded === faq.faq_id ? styles.open : ""
                      }`}
                    />
                  </button>
                </div>
                <div className={styles.faqMeta}>
                  <span>{faq.question_type}</span>
                  <span> | </span>
                  <span>{faq.category}</span>
                </div>
                <div
                  className={`${styles.faqAnswer} ${
                    expanded === faq.faq_id ? styles.expanded : ""
                  }`}
                >
                  {faq.answers && faq.answers.length > 0 ? (
                    faq.answers.map((answer, index) => (
                      <p key={index}>{answer}</p>
                    ))
                  ) : (
                    <div>No answer available</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FAQSection;
